import { BranchTotals } from "./riders/BranchTotals";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { UploadTracker } from "./UploadTracker";
import { ExtraFilters, matchFields } from "./EmpExtraFilters";
const RIDER_FIELDS = [
  { k: "vehicle", label: "المركبة", get: (r) => r.vehicle },
  { k: "supervisor", label: "المشرف", get: (r) => r.supervisor },
  { k: "city", label: "المدينة", get: (r) => r.city },
  { k: "contract", label: "العقد", get: (r) => r.contract },
  { k: "branch", label: "الفرع", get: (r) => r.branch },
  { k: "rider_status", label: "حالة المندوب", get: (r) => r.rider_status },
  { k: "grade", label: "التقييم", get: (r) => r.grade },
  { k: "target", label: "التارقت", get: (r) => (r.order_target ? (r.target_met ? "حقق التارقت" : "لم يحقق") : "") },
  { k: "linked", label: "الربط", get: (r) => (r.emp_id ? "مربوط بموظف" : "غير مربوط") },
  { k: "suspended", label: "الإيقاف", get: (r) => (r.suspended || r.platform_suspended ? "موقوف" : "غير موقوف") },
  { k: "freelance", label: "فري لانسر", get: (r) => (!r.emp_id ? "غير مربوط بموظف" : r.rented?.length ? ["فري لانسر (حساب مستأجر)", ...r.rented.map((p) => `مستأجر على ${p}`)] : "حساب خاص") },
];
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PlatformReportsSection } from "./PlatformReportsSection";
import { HsReportsSection } from "./HsReportsSection";
import { MonthlyEvaluationSection } from "./MonthlyEvaluationSection";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Bike, Upload, FileSpreadsheet, Loader2, Search, Trash2, CalendarDays, Users,
  PackageCheck, CheckCircle2, TrendingUp, TrendingDown, Clock, AlertTriangle, Layers, Link2, Minus, Bug,
  BarChart3, Package, Printer, Archive, ChevronDown, CalendarRange, Scale, CheckCircle, RotateCcw,
} from "lucide-react";
import { api, ridersExportUrl } from "../lib/api";
import { saveBlob } from "../lib/download";
import { useLang } from "../i18n/LanguageContext";
import { usePlatform, getPlatform, setPlatform, platformStyle, isKeeta } from "../lib/ridersPlatform";
import { RiderPlatformSwitcher } from "./RiderPlatformSwitcher";
import { KeetaPurgeButton } from "./KeetaPurgeButton";
import { KeetaReports } from "./KeetaReports";
import { KeetaSupervisors } from "./KeetaSupervisors";
import { RiderTargetsDialog, RTBadge, RTBars } from "./KeetaRiderTargets";
import { Target, X } from "lucide-react";
import { RiderDayRange } from "./RiderDayRange";
import { KeetaDashboard } from "./KeetaDashboard";
import { UnifiedRidersView } from "./UnifiedRidersView";
import { UNIFIED } from "../lib/ridersPlatform";
import { NinjaTools } from "./NinjaTools";

const today = () => new Date().toISOString().slice(0, 10);
const CUMULATIVE = "__cumulative__";
const CUMULATIVE_ALL = "__cumulative_all__";
const monthBounds = (ym) => {
  if (!ym) return [null, null];
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return [`${ym}-01`, `${ym}-${String(last).padStart(2, "0")}`];
};

const COL_KEYS = [
  { k: "city", t: "text" }, { k: "contract", t: "text" }, { k: "vehicle", t: "text" },
  { k: "tga_status", t: "status" }, { k: "error_codes", t: "text" }, { k: "shifts", t: "num" },
  { k: "working_days", t: "num" }, { k: "planned_hours", t: "num" }, { k: "actual_hours", t: "num" },
  { k: "avg_hours_day", t: "num" }, { k: "attendance_rate", t: "pct" }, { k: "break_hours", t: "num" },
  { k: "lost_hours", t: "num" }, { k: "acceptance_rate", t: "pct" }, { k: "contact_rate", t: "pct" },
  { k: "no_shows", t: "num" }, { k: "no_show_pct", t: "pct" }, { k: "notified_deliveries", t: "num" },
  { k: "completed_deliveries", t: "num" }, { k: "accepted_deliveries", t: "num" }, { k: "stacked_deliveries", t: "num" },
  { k: "declined_deliveries", t: "num" }, { k: "cancelled_deliveries", t: "num" }, { k: "deduction_deliveries", t: "num" },
  { k: "not_accepted_deliveries", t: "num" }, { k: "manual_undispatched", t: "num" }, { k: "chat_response_pct", t: "pct" },
];

const fmt = (v, t) => {
  if (t === "text" || t === "status") return v === null || v === undefined || v === "" ? "—" : v;
  if (v === null || v === undefined || v === "") return "—";
  if (t === "pct") return `${(Number(v) * 100).toFixed(1)}%`;
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const riderName = (r) => r.emp_name || r.rider_name || r.rider_id;

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const monthLabel = (ym) => {
  const [y, m] = String(ym).split("-");
  return `${AR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};
const groupByMonth = (dates) => {
  const map = new Map();
  for (const d of dates) {
    const ym = d.slice(0, 7);
    if (!map.has(ym)) map.set(ym, []);
    map.get(ym).push(d);
  }
  return [...map.entries()];
};

const GRADE_CLS = {
  A: "bg-emerald-100 text-emerald-700 border-emerald-200", B: "bg-teal-100 text-teal-700 border-teal-200",
  C: "bg-amber-100 text-amber-700 border-amber-200", D: "bg-orange-100 text-orange-700 border-orange-200",
  E: "bg-rose-100 text-rose-600 border-rose-200", F: "bg-red-100 text-red-700 border-red-200",
};

const GradeBadge = ({ grade, percent }) => {
  if (!grade) return <span className="text-slate-300">—</span>;
  return (
    <span title={percent != null ? `${percent}%` : ""}
      className={`inline-flex h-7 min-w-[28px] items-center justify-center rounded-lg border px-1.5 font-heading text-sm font-extrabold ${GRADE_CLS[grade] || "bg-slate-100 text-slate-500"}`}>
      {grade}
    </span>
  );
};

const TARGET_TIER = (comp, target) => {
  const tt = Number(target) || 0;
  if (tt <= 0) return null;
  const pct = (Number(comp) || 0) / tt;
  if (pct >= 0.98) return "met";
  if (pct >= 0.95) return "near";
  return "below";
};
const TARGET_TIER_CLS = {
  met: "bg-[#f3e4cf] text-[#8a541b] border-[#d8b483]",
  near: "bg-emerald-100 text-emerald-700 border-emerald-200",
  below: "bg-red-100 text-red-700 border-red-200",
};

const TargetCell = ({ row }) => {
  const tgt = Number(row.order_target) || 0;
  const comp = Number(row.completed_deliveries) || 0;
  if (!tgt) return <span className="text-slate-300">—</span>;
  const tier = TARGET_TIER(comp, tgt);
  return (
    <span data-testid={`rider-target-${row.rider_id}`}
      className={`inline-flex items-center justify-center rounded-lg border px-2 py-0.5 font-heading text-xs font-bold font-mono-nums ${TARGET_TIER_CLS[tier]}`}>
      {comp} / {tgt}
    </span>
  );
};

const ChangeCell = ({ row, metric }) => {
  const { t } = useLang();
  const raw = metric === "attendance_rate" ? row.change_attendance : row.change_completed;
  if (raw === null || raw === undefined) return <span className="text-slate-300">—</span>;
  const isPct = metric === "attendance_rate";
  const disp = isPct ? `${raw > 0 ? "+" : ""}${(raw * 100).toFixed(1)}%` : `${raw > 0 ? "+" : ""}${Number.isInteger(raw) ? raw : raw.toFixed(1)}`;
  const cls = raw > 0 ? "text-blue-600" : raw < 0 ? "text-red-600" : "text-slate-400";
  const Icon = raw > 0 ? TrendingUp : raw < 0 ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold tabular-nums ${cls}`}
      title={row.change_prev_date ? t("dash.riders.comparePrev", { d: row.change_prev_date }) : ""}>
      <Icon className="h-3.5 w-3.5" /> {disp}
    </span>
  );
};

const TONE = {
  emerald: "bg-blue-100 text-blue-700",
  sky: "bg-sky-100 text-sky-700",
  amber: "bg-amber-100 text-amber-700",
};

const KpiCard = ({ icon: Icon, label, value, tone = "emerald", testid, onClick }) => {
  const { t } = useLang();
  return (
    <div data-testid={testid} onClick={onClick}
      className={`rounded-2xl border bg-white p-4 border-slate-200 ${onClick ? "cursor-pointer hover:border-blue-300 hover:shadow-md transition" : ""}`}>
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl mb-2 ${TONE[tone]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="text-2xl font-heading font-extrabold text-slate-900 tabular-nums">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
      {onClick && <p className="text-[10px] text-blue-600 mt-1">{t("dash.riders.viewList")}</p>}
    </div>
  );
};

const hasError = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  return s !== "" && s !== "0" && s !== "nan";
};

const splitCodes = (v) => String(v ?? "").split(/[،,;/|\s]+/).map((c) => c.trim()).filter(Boolean);

const UploadDialog = ({ open, onOpenChange, onDone }) => {
  const { t, dir } = useLang();
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("file"); // "file" | "drive"
  const [driveUrl, setDriveUrl] = useState("");
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { if (open) { setDate(today()); setMode("file"); setDriveUrl(""); if (fileRef.current) fileRef.current.value = ""; } }, [open]);
  useEffect(() => {
    if (!open) return;
    api.branches().then((r) => {
      const names = (Array.isArray(r) ? r : r?.items || []).map((b) => b.branch).filter((b) => b && b !== "غير محدد");
      setBranches(names);
      setBranch((cur) => (names.includes(cur) ? cur : names.length === 1 ? names[0] : ""));
    }).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!date) { toast.warning(t("dash.riders.uWarnDate")); return; }
    if (branches.length > 1 && !branch) { toast.warning("اختر الفرع الذي يخصه هذا التقرير"); return; }
    const file = fileRef.current?.files?.[0];
    if (mode === "file" && !file) { toast.warning(t("dash.riders.uWarnFile")); return; }
    if (mode === "drive" && !driveUrl.trim()) { toast.warning("الصق رابط Google Drive"); return; }
    const platform = getPlatform();
    setBusy(true);
    try {
      const res = mode === "drive"
        ? await api.uploadRidersFromDrive(driveUrl.trim(), date, platform, branch)
        : await api.uploadRiders(file, date, platform, branch);
      if (res.kind === "supervisors") {
        toast.success(`هذا تقرير أداء المشرفين (بدون معرّفات مناديب) — تم رفعه في تبويب «تقرير المشرفين»: ${res.supervisors} مشرف، ${res.rows} صف (${res.from} ← ${res.to})`, { duration: 9000 });
        onOpenChange(false);
        return;
      }
      if (res.platform && res.platform !== platform) {
        setPlatform(res.platform);
        toast.success(`تم اكتشاف منصة «${res.platform}» تلقائياً — ${res.inserted} مندوب`);
      } else {
        toast.success(t("dash.riders.uploaded", { n: res.inserted, d: res.report_date }));
      }
      onOpenChange(false);
      onDone(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash.riders.uploadFail"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body" dir={dir}>
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">{t("dash.riders.uTitle")}</DialogTitle>
          <DialogDescription className="text-right">{t("dash.riders.uDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="rounded-lg px-3 py-2 text-sm font-semibold flex items-center gap-2" data-testid="upload-platform-banner"
            style={{ background: platformStyle(getPlatform()).bg, color: platformStyle(getPlatform()).text }}>
            {platformStyle(getPlatform()).logo
              ? <img src={platformStyle(getPlatform()).logo} alt="" className="h-5 w-5 rounded object-contain" />
              : <span aria-hidden>{platformStyle(getPlatform()).emoji}</span>}
            سيتم الرفع إلى منصة: {getPlatform()} <span className="text-xs font-normal opacity-70">(يُكتشف تلقائياً من الملف)</span>
          </div>
          {branches.length > 0 && (
            <div className="space-y-1.5" data-testid="rider-upload-branch">
              <Label>الفرع {branches.length > 1 && <span className="text-rose-500">*</span>}</Label>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger data-testid="rider-upload-branch-select"><SelectValue placeholder="اختر الفرع" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">الرفع يستبدل تقرير هذا الفرع فقط لنفس الأيام، وتبقى تقارير الفروع الأخرى.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="rider-report-date">{t("dash.riders.reportDate")}</Label>
            <Input id="rider-report-date" type="date" data-testid="rider-report-date-input"
              value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1 w-full">
            <button type="button" data-testid="upload-mode-file" onClick={() => setMode("file")}
              className={`flex-1 rounded-md py-1.5 text-sm font-semibold transition ${mode === "file" ? "bg-white shadow-sm text-slate-800" : "text-slate-500"}`}>
              رفع ملف
            </button>
            <button type="button" data-testid="upload-mode-drive" onClick={() => setMode("drive")}
              className={`flex-1 rounded-md py-1.5 text-sm font-semibold transition ${mode === "drive" ? "bg-white shadow-sm text-slate-800" : "text-slate-500"}`}>
              رابط Google Drive
            </button>
          </div>
          {mode === "file" ? (
            <div className="space-y-1.5">
              <Label htmlFor="rider-file">{t("dash.riders.excelFile")}</Label>
              <Input id="rider-file" type="file" ref={fileRef} data-testid="rider-file-input"
                accept=".xlsx,.xls,.csv,.txt,text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream" className="cursor-pointer" />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="rider-drive-url">رابط الملف على Google Drive أو Google Sheets</Label>
              <Input id="rider-drive-url" dir="ltr" data-testid="rider-drive-url-input"
                placeholder="https://drive.google.com/... أو https://docs.google.com/spreadsheets/..." value={driveUrl}
                onChange={(e) => setDriveUrl(e.target.value)} />
              <p className="text-[11px] text-slate-400">يدعم ملفات Excel المرفوعة وملفات Google Sheets. تأكد أن صلاحية الملف «أي شخص لديه الرابط» (Anyone with the link).</p>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="confirm-upload-riders-button" disabled={busy} onClick={submit}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("dash.riders.upload")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.riders.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const fmtN = (v) => {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const ReconcileDialog = ({ open, onOpenChange, onApplied }) => {
  const { dir } = useLang();
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [corrCount, setCorrCount] = useState(0);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const loadCorrCount = async () => {
    try {
      const res = await api.reconcileCorrectionsCount(getPlatform());
      setCorrCount(res?.count || 0);
    } catch { setCorrCount(0); }
  };

  useEffect(() => { if (open) { setResult(null); if (fileRef.current) fileRef.current.value = ""; loadCorrCount(); } }, [open]);

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.warning("اختر ملف Excel للمطابقة"); return; }
    setBusy(true);
    try {
      const res = await api.reconcileRiders(file, getPlatform());
      setResult(res);
      if (res.mismatch_count === 0) toast.success("مطابقة تامّة — لا يوجد أي فرق 🎉");
      else toast.warning(`${res.mismatch_count} مندوب مختلف — فرق الطلبات المكتملة ${res.completed_diff}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّرت المطابقة");
    } finally { setBusy(false); }
  };

  const applyAll = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.warning("اختر ملف Excel أولاً"); return; }
    if (!window.confirm("سيتم تصحيح أرقام النظام لتطابق الملف لكل المندوبين المختلفين على هذه المنصة. هل تريد المتابعة؟")) return;
    setApplying(true);
    try {
      const res = await api.applyReconcile(file, getPlatform());
      toast.success(`تم تطبيق التصحيح على ${res.applied} مندوب — أصبح النظام مطابقاً للملف`);
      await submit();
      await loadCorrCount();
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تطبيق التصحيح");
    } finally { setApplying(false); }
  };

  const undoAll = async () => {
    if (corrCount <= 0) return;
    if (!window.confirm(`سيتم حذف قيود التصحيح لـ ${corrCount} مندوب على منصة «${getPlatform()}»، ليعود النظام للأرقام اليومية الأصلية. هل تريد المتابعة؟`)) return;
    setUndoing(true);
    try {
      const res = await api.undoReconcile(getPlatform());
      toast.success(`تم التراجع عن التصحيح — حُذفت قيود ${res.deleted} مندوب وعاد النظام لأرقامه الأصلية`);
      setCorrCount(0);
      if (fileRef.current?.files?.[0]) await submit();
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر التراجع عن التصحيح");
    } finally { setUndoing(false); }
  };

  const fieldLabel = (k) => (result?.fields || []).find((f) => f.key === k)?.label || k;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="reconcile-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Scale className="h-5 w-5 text-blue-600" /> مطابقة تقرير تجميعي
          </DialogTitle>
          <DialogDescription className="text-right">
            ارفع تقرير المنصة التجميعي (مثل ملف هنجرستيشن الشهري) ليقارنه النظام تلقائياً مع مجموع الأيام المرفوعة، ويُبرز أي مندوب تختلف أرقامه.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="rounded-lg px-3 py-2 text-sm font-semibold flex items-center gap-2"
            style={{ background: platformStyle(getPlatform()).bg, color: platformStyle(getPlatform()).text }}>
            {platformStyle(getPlatform()).logo
              ? <img src={platformStyle(getPlatform()).logo} alt="" className="h-5 w-5 rounded object-contain" />
              : <span aria-hidden>{platformStyle(getPlatform()).emoji}</span>}
            المقارنة على منصة: {getPlatform()}
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="reconcile-file">ملف Excel التجميعي</Label>
              <Input id="reconcile-file" type="file" ref={fileRef} data-testid="reconcile-file-input"
                accept=".xlsx,.xls,.csv,.txt,text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream" className="cursor-pointer" />
            </div>
            <Button data-testid="confirm-reconcile-button" disabled={busy} onClick={submit}
              className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />} طابِق
            </Button>
          </div>

          {result && (
            <div className="space-y-3" data-testid="reconcile-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-lg font-bold tabular-nums text-slate-800">{fmtN(result.file_completed)}</p>
                  <p className="text-[11px] text-slate-500">مكتملة (الملف)</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-lg font-bold tabular-nums text-slate-800">{fmtN(result.system_completed)}</p>
                  <p className="text-[11px] text-slate-500">مكتملة (النظام)</p>
                </div>
                <div className={`rounded-xl border p-3 ${result.completed_diff === 0 ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                  <p className={`text-lg font-bold tabular-nums ${result.completed_diff === 0 ? "text-emerald-700" : "text-amber-700"}`}>{result.completed_diff > 0 ? "+" : ""}{fmtN(result.completed_diff)}</p>
                  <p className="text-[11px] text-slate-500">الفرق</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 text-center">
                {result.file_riders} مندوب في الملف · {result.system_riders} مندوب في النظام
              </p>

              {result.mismatch_count === 0 ? (
                <div className="py-8 text-center" data-testid="reconcile-perfect">
                  <CheckCircle className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
                  <p className="font-semibold text-slate-700">مطابقة تامّة</p>
                  <p className="text-sm text-slate-400">جميع المندوبين وكل الأرقام متطابقة مع الملف.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-600">مندوبون مختلفون ({result.mismatch_count})</p>
                    <Button data-testid="apply-reconcile-button" size="sm" disabled={applying} onClick={applyAll}
                      className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
                      {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} تطبيق الملف على النظام
                    </Button>
                  </div>
                  <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-1.5">
                    «تطبيق الملف» يصحّح أرقام النظام لتطابق الملف التجميعي لكل المندوبين المختلفين (الملف هو المرجع).
                  </p>
                  {result.mismatches.map((m) => (
                    <div key={m.rider_id} data-testid={`reconcile-row-${m.rider_id}`}
                      className="rounded-xl border border-slate-100 bg-white p-3">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="font-semibold text-slate-800 text-sm truncate">{m.name || m.rider_id}</span>
                        <span className="text-[10px] text-slate-400 font-mono-nums shrink-0">#{m.rider_id}</span>
                      </div>
                      {!m.in_system && <p className="text-[11px] text-rose-600 mb-1">موجود في الملف فقط — لم يُرفع للنظام</p>}
                      {!m.in_file && <p className="text-[11px] text-rose-600 mb-1">موجود في النظام فقط — غير موجود في الملف</p>}
                      <div className="space-y-1">
                        {Object.entries(m.diffs).map(([k, d]) => (
                          <div key={k} className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-slate-500">{fieldLabel(k)}</span>
                            <span className="flex items-center gap-2 tabular-nums">
                              <span className="text-slate-600">الملف {fmtN(d.file)}</span>
                              <span className="text-slate-300">·</span>
                              <span className="text-slate-600">النظام {fmtN(d.system)}</span>
                              <span className={`font-bold ${d.diff > 0 ? "text-amber-600" : "text-blue-600"}`}>({d.diff > 0 ? "+" : ""}{fmtN(d.diff)})</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
                    ملاحظة: الفروق البسيطة (طلب أو طلبان) شائعة بين تقرير المنصة التجميعي ومجموع تقاريرها اليومية، بسبب طلبات قرب منتصف الليل أو طلبات مُجمّعة (Stacked) يُعاد تصنيفها في المصدر.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button variant="outline" onClick={() => onOpenChange(false)}>إغلاق</Button>
          {corrCount > 0 && (
            <Button data-testid="undo-reconcile-button" variant="outline" disabled={undoing} onClick={undoAll}
              className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700">
              {undoing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              تراجع عن التصحيح ({corrCount})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const UploadNotice = ({ u, onClose }) => {
  const months = u.months || (u.report_date ? { [String(u.report_date).slice(0, 7)]: 1 } : {});
  const ml = (m) => { const [y, mo] = m.split("-").map(Number); return new Date(y, mo - 1, 1).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { month: "long", year: "numeric" }); };
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="riders-upload-notice">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="flex items-center gap-1.5 font-bold"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> تم الرفع إلى «{u.platform}»</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(months).map(([m, n]) => (
              <span key={m} className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200" data-testid={`riders-upload-month-${m}`}>{ml(m)} · {n} {n === 1 ? "يوم" : "يوم"}</span>
            ))}
          </div>
          <p className="text-xs text-emerald-800" data-testid="riders-upload-detail">
            {u.first_date && u.first_date !== u.last_date ? <>من <b dir="ltr">{u.first_date}</b> إلى <b dir="ltr">{u.last_date}</b> · </> : <>تاريخ <b dir="ltr">{u.first_date || u.report_date}</b> · </>}
            {u.riders != null && <>{u.riders.toLocaleString("en-US")} مندوب · </>}{Number(u.inserted).toLocaleString("en-US")} صف
            {" · "}الأيام المرفوعة مرة ثانية تحدّثت أرقامها بدل ما تتكرر.
          </p>
        </div>
        <button onClick={onClose} className="rounded-lg p-1 text-emerald-600 hover:bg-emerald-100" data-testid="riders-upload-notice-close"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

const RiderDetailDialog = ({ riderId, month, onClose, row }) => {
  const { t, dir } = useLang();
  const [platform] = usePlatform();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!riderId) return;
    setLoading(true);
    api.riderDetail(riderId, platform, month).then(setData).catch(() => toast.error(t("dash.riders.detailFail"))).finally(() => setLoading(false));
  }, [riderId, t, platform, month]);

  const tot = data?.totals;
  return (
    <Dialog open={Boolean(riderId)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
              {(data?.emp_name || riderId || "؟").charAt(0)}
            </div>
            {data?.emp_name || t("dash.riders.riderPrefix", { id: riderId })}
          </DialogTitle>
          <DialogDescription className="text-right">
            {t("dash.riders.riderNoPrefix", { id: riderId })}{data?.emp_name ? "" : t("dash.riders.notLinkedEmp")}{t("dash.riders.compareAcross")}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center text-slate-400">{t("dash.riders.loading")}</div>
        ) : !data || data.days.length === 0 ? (
          <div className="py-12 text-center text-slate-400">{t("dash.riders.noRiderData")}</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-slate-200 p-3 text-center">
                <p className="text-xl font-extrabold text-slate-900">{data.days.length}</p>
                <p className="text-xs text-slate-500">{t("dash.riders.daysCount")}</p>
              </div>
              <div className="rounded-xl border border-slate-200 p-3 text-center">
                <p className="text-xl font-extrabold text-slate-900">{fmt(tot?.total_completed, "num")}</p>
                <p className="text-xs text-slate-500">{t("dash.riders.totalCompleted")}</p>
              </div>
              <div className="rounded-xl border border-slate-200 p-3 text-center">
                <p className="text-xl font-extrabold text-slate-900">{(tot?.avg_attendance * 100).toFixed(1)}%</p>
                <p className="text-xs text-slate-500">{t("dash.riders.avgAttendance")}</p>
              </div>
            </div>

            {platform === "نينجا" && (() => {
              const shifts = data.days.flatMap((d) => (d.ninja_shifts || []).map((s) => ({ ...s, date: d.report_date })));
              const hhmm = (v) => { const m = String(v || "").match(/(\d{1,2}:\d{2})/); return m ? m[1] : "—"; };
              if (!shifts.length) return null;
              return (
                <div className="rounded-xl border border-slate-200 overflow-x-auto" data-testid="rider-ninja-shifts">
                  <div className="px-3 py-2 bg-slate-50 font-heading font-bold text-sm text-slate-700">الورديات ({shifts.length})</div>
                  <table className="w-full text-sm text-right min-w-[680px]">
                    <thead><tr className="bg-slate-50 border-y border-slate-200 text-xs text-slate-600">
                      <th className="px-3 py-2">التاريخ</th><th className="px-3 py-2">الوردية</th><th className="px-3 py-2">البداية</th><th className="px-3 py-2">النهاية</th>
                      <th className="px-3 py-2">تسجيل الدخول</th><th className="px-3 py-2">الطلبات</th><th className="px-3 py-2">الساعات</th><th className="px-3 py-2">في الوقت</th><th className="px-3 py-2">التأخير(د)</th>
                    </tr></thead>
                    <tbody>{shifts.map((s, i) => (
                      <tr key={`${s.date}-${s.name || ""}-${i}`} className="border-b border-slate-100 last:border-0">
                        <td className="px-3 py-2 whitespace-nowrap" dir="ltr">{s.date}</td>
                        <td className="px-3 py-2">{s.name || "—"}</td>
                        <td className="px-3 py-2" dir="ltr">{hhmm(s.start)}</td>
                        <td className="px-3 py-2" dir="ltr">{hhmm(s.end)}</td>
                        <td className="px-3 py-2" dir="ltr">{s.checkin ? hhmm(s.checkin) : <span className="text-red-500">لم يسجّل</span>}</td>
                        <td className="px-3 py-2 font-semibold">{s.delivered}</td>
                        <td className="px-3 py-2">{s.hours}</td>
                        <td className="px-3 py-2">{s.ontime != null ? `${(s.ontime * 100).toFixed(0)}%` : "—"}</td>
                        <td className="px-3 py-2">{s.delay}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              );
            })()}

            {isKeeta(platform) && <RTBars kt={row?.ktargets} />}
            {isKeeta(platform) && data.days.length > 1 && (
              <div className="rounded-xl border border-slate-200 p-3" data-testid="rider-keeta-chart">
                <p className="mb-2 font-heading text-sm font-bold text-slate-700">الطلبات وساعات الاتصال يومياً</p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.days.map((d) => ({ day: (d.report_date || "").slice(5), orders: d.completed_deliveries || 0, hours: +(d.actual_hours || 0).toFixed(1) }))} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} reversed />
                    <YAxis yAxisId="o" tick={{ fontSize: 10 }} orientation="right" />
                    <YAxis yAxisId="h" tick={{ fontSize: 10 }} orientation="left" />
                    <Tooltip formatter={(v, k) => [v, k === "orders" ? "الطلبات المسلّمة" : "ساعات الاتصال"]} />
                    <Line yAxisId="o" type="monotone" dataKey="orders" stroke="#059669" strokeWidth={2} dot={false} />
                    <Line yAxisId="h" type="monotone" dataKey="hours" stroke="#2563EB" strokeWidth={2} dot={false} strokeDasharray="4 3" />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-1 flex justify-center gap-4 text-[11px] text-slate-500"><span className="flex items-center gap-1"><span className="h-0.5 w-4 bg-emerald-600" />الطلبات المسلّمة (يمين)</span><span className="flex items-center gap-1"><span className="h-0.5 w-4 border-t-2 border-dashed border-blue-600" />ساعات الاتصال (يسار)</span></div>
              </div>
            )}
            {isKeeta(platform) && (() => {
              const kv = (d, sec, lab) => d.keeta?.[`${sec}|${lab}`]?.v;
              return (
                <div className="rounded-xl border border-slate-200 overflow-x-auto" data-testid="rider-keeta-days">
                  <div className="px-3 py-2 bg-slate-50 font-heading font-bold text-sm text-slate-700">المؤشرات اليومية ({data.days.length})</div>
                  <table className="w-full text-sm text-right min-w-[640px]">
                    <thead><tr className="bg-slate-50 border-y border-slate-200 text-xs text-slate-600">
                      <th className="px-3 py-2">التاريخ</th><th className="px-3 py-2">المُسلّمة</th><th className="px-3 py-2">المقبولة</th>
                      <th className="px-3 py-2">في الوقت المحدد</th><th className="px-3 py-2">معدل الاكتمال</th><th className="px-3 py-2">المتأخرة</th><th className="px-3 py-2">مدة التوصيل</th>
                    </tr></thead>
                    <tbody>{data.days.map((d, i) => {
                      const ontime = kv(d, "تجربة التوصيل", "نسبة الطلبات التي تم تسليمها في الوقت المحدد (D)");
                      const comp = kv(d, "أحجام المهام", "معدل اكتمال الطلبات (غير متعلق بالتوصيل)");
                      const late = kv(d, "تجربة التوصيل", "مهام الطلبات المتأخرة");
                      const dur = kv(d, "تجربة التوصيل", "متوسط مدة التوصيل لكل طلب مكتمل");
                      return (
                        <tr key={d.report_date || i} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-2 whitespace-nowrap" dir="ltr">{d.report_date}</td>
                          <td className="px-3 py-2 font-semibold">{fmt(d.completed_deliveries, "num")}</td>
                          <td className="px-3 py-2">{fmt(d.accepted_deliveries, "num")}</td>
                          <td className="px-3 py-2">{ontime != null ? `${(ontime * 100).toFixed(1)}%` : "—"}</td>
                          <td className="px-3 py-2">{comp != null ? `${(comp * 100).toFixed(1)}%` : "—"}</td>
                          <td className="px-3 py-2 text-amber-700">{late != null ? late : "—"}</td>
                          <td className="px-3 py-2">{dur != null ? `${dur} د` : "—"}</td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              );
            })()}

            {data.trend.length > 1 && (
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="font-heading font-bold text-slate-700 text-sm mb-2">{t("dash.riders.perfAcross")}</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={data.trend} margin={{ left: -12, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="completed" name={t("dash.riders.completed")} stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                    <Line type="monotone" dataKey="attendance" name={t("dash.riders.attendancePct")} stroke="#0284c7" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {platform !== "نينجا" && !isKeeta(platform) && (
            <>
            <div className="hidden md:block rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm text-right min-w-[720px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.date")}</th>
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.completed")}</th>
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.notified")}</th>
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.declined")}</th>
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.attendance")}</th>
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.acceptance")}</th>
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.plannedH")}</th>
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.actualH")}</th>
                    <th className="px-3 py-2 font-semibold">{t("dash.riders.noShow")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((d) => (
                    <tr key={d.report_date} data-testid={`rider-day-${d.report_date}`} className="border-b border-slate-100">
                      <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{d.report_date}</td>
                      <td className="px-3 py-2">{fmt(d.completed_deliveries, "num")}</td>
                      <td className="px-3 py-2">{fmt(d.notified_deliveries, "num")}</td>
                      <td className="px-3 py-2 text-red-600">{fmt(d.declined_deliveries, "num")}</td>
                      <td className="px-3 py-2">{fmt(d.attendance_rate, "pct")}</td>
                      <td className="px-3 py-2">{fmt(d.acceptance_rate, "pct")}</td>
                      <td className="px-3 py-2">{fmt(d.planned_hours, "num")}</td>
                      <td className="px-3 py-2">{fmt(d.actual_hours, "num")}</td>
                      <td className="px-3 py-2">{fmt(d.no_shows, "num")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: stacked cards */}
            <div className="md:hidden space-y-2.5">
              {data.days.map((d) => (
                <div key={d.report_date} data-testid={`rider-day-m-${d.report_date}`}
                  className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-2">
                    <span className="font-heading font-bold text-slate-800 text-sm" dir="ltr">{d.report_date}</span>
                    <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-bold text-blue-700">
                      {t("dash.riders.completed")}: {fmt(d.completed_deliveries, "num")}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-slate-500">{t("dash.riders.notified")}</span><span className="font-semibold">{fmt(d.notified_deliveries, "num")}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("dash.riders.declined")}</span><span className="font-semibold text-red-600">{fmt(d.declined_deliveries, "num")}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("dash.riders.attendance")}</span><span className="font-semibold">{fmt(d.attendance_rate, "pct")}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("dash.riders.acceptance")}</span><span className="font-semibold">{fmt(d.acceptance_rate, "pct")}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("dash.riders.plannedH")}</span><span className="font-semibold">{fmt(d.planned_hours, "num")}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("dash.riders.actualH")}</span><span className="font-semibold">{fmt(d.actual_hours, "num")}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("dash.riders.noShow")}</span><span className="font-semibold">{fmt(d.no_shows, "num")}</span></div>
                  </div>
                </div>
              ))}
            </div>
            </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RankList = ({ title, icon: Icon, tone, rows, metric, onSelect }) => {
  const { t } = useLang();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <p className={`font-heading font-bold text-sm mb-3 flex items-center gap-1.5 ${tone === "amber" ? "text-amber-700" : "text-blue-700"}`}>
        <Icon className="h-4 w-4" /> {title}
      </p>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <button key={r.rider_id} type="button" onClick={() => onSelect?.(r.rider_id)}
            data-testid={`rank-row-${r.rider_id}`}
            className="w-full flex items-center gap-2 text-sm rounded-lg px-1.5 py-1 -mx-1.5 text-right hover:bg-slate-50 transition-colors cursor-pointer">
            <span className="h-6 w-6 shrink-0 rounded-full bg-slate-100 text-slate-500 text-xs flex items-center justify-center font-bold">{i + 1}</span>
            <span className="text-slate-700 truncate hover:text-blue-700 hover:underline">{riderName(r)}</span>
            <span className={`mr-auto font-bold tabular-nums ${tone === "amber" ? "text-amber-600" : "text-blue-600"}`}>
              {metric === "attendance_rate" ? fmt(r[metric], "pct") : fmt(r[metric], "num")}
            </span>
          </button>
        ))}
        {rows.length === 0 && <p className="text-xs text-slate-400 text-center py-2">{t("dash.riders.noData")}</p>}
      </div>
    </div>
  );
};

const SRC_CLS = {
  manual: "border-blue-200 bg-blue-50 text-blue-700",
  rider_id: "border-emerald-200 bg-emerald-50 text-emerald-700",
  auto: "border-sky-200 bg-sky-50 text-sky-700",
  name: "border-violet-200 bg-violet-50 text-violet-700",
  none: "border-slate-200 bg-slate-50 text-slate-500",
};

const MappingDialog = ({ open, onOpenChange, onSaved }) => {
  const { t, dir } = useLang();
  const [riders, setRiders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sel, setSel] = useState({});
  const [initial, setInitial] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [onlyUnmatched, setOnlyUnmatched] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.ridersMappings().then((res) => {
      setRiders(res.riders || []);
      setEmployees(res.employees || []);
      const s = {};
      (res.riders || []).forEach((r) => { s[r.rider_id] = r.employee_id || ""; });
      setSel(s);
      setInitial({ ...s });
    }).catch(() => toast.error(t("dash.riders.mapFail"))).finally(() => setLoading(false));
  }, [open, t]);

  const setRow = (rid, eid) => setSel((p) => ({ ...p, [rid]: eid }));

  const visible = riders.filter((r) => {
    if (onlyUnmatched && r.source !== "none") return false;
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return [r.rider_id, r.rider_name, r.employee_name].some((v) => String(v || "").toLowerCase().includes(s));
  });

  const dirtyCount = Object.keys(sel).filter((rid) => (sel[rid] || "") !== (initial[rid] || "")).length;

  const save = async () => {
    const mappings = Object.keys(sel)
      .filter((rid) => (sel[rid] || "") !== (initial[rid] || ""))
      .map((rid) => ({ rider_id: rid, employee_id: sel[rid] || "" }));
    if (mappings.length === 0) { toast.info(t("dash.riders.noChanges")); return; }
    setSaving(true);
    try {
      await api.saveRiderMappings(mappings);
      toast.success(t("dash.riders.savedN", { n: mappings.length }));
      onOpenChange(false);
      onSaved();
    } catch { toast.error(t("dash.riders.saveFail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">{t("dash.riders.mapTitle")}</DialogTitle>
          <DialogDescription className="text-right">{t("dash.riders.mapDesc")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="only-unmatched-toggle" checked={onlyUnmatched}
              onChange={(e) => setOnlyUnmatched(e.target.checked)} className="accent-blue-600" />
            {t("dash.riders.onlyUnmatched")}
          </label>
          <div className="relative flex-1 min-w-[160px]">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="mapping-search" placeholder={t("dash.riders.searchNumName")} value={search}
              onChange={(e) => setSearch(e.target.value)} className="pr-9 h-9" />
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-slate-400">{t("dash.riders.loading")}</div>
        ) : visible.length === 0 ? (
          <div className="py-10 text-center text-slate-400" data-testid="mapping-empty">
            {onlyUnmatched ? t("dash.riders.allLinked") : t("dash.riders.noRiders")}
          </div>
        ) : (
          <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
            {visible.map((r) => (
              <div key={r.rider_id} data-testid={`mapping-row-${r.rider_id}`}
                className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
                <span className="font-mono-nums font-semibold text-slate-800 w-24 shrink-0">#{r.rider_id}</span>
                {r.rider_name && <span className="text-xs text-slate-500 truncate max-w-[90px] shrink-0">{r.rider_name}</span>}
                <Badge variant="outline" className={`rounded-full text-[10px] shrink-0 ${SRC_CLS[r.source]}`}>{t(`dash.riders.src.${r.source}`)}</Badge>
                <Select value={sel[r.rider_id] || "__none__"} onValueChange={(v) => setRow(r.rider_id, v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid={`mapping-select-${r.rider_id}`} className="mr-auto w-56 h-9"><SelectValue placeholder={t("dash.riders.pickEmployee")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("dash.riders.noLink")}</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}{e.employee_number ? ` — ${e.employee_number}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="save-mappings-button" disabled={saving} onClick={save}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("dash.riders.save")} {dirtyCount > 0 ? `(${dirtyCount})` : ""}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.riders.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RidersPanel = ({ canEdit }) => {
  const { t, dir } = useLang();
  const [platform] = usePlatform();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(CUMULATIVE);
  const [city, setCity] = useState("all");
  const [branch, setBranch] = useState("all");
  const [contract, setContract] = useState("all");
  const [search, setSearch] = useState("");
  const [rankMetric, setRankMetric] = useState("completed_deliveries");
  const [rxf, setRxf] = useState({});
  const [range, setRange] = useState({ from: "", to: "" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [keetaTab, setKeetaTab] = useState("sup");
  const [rtOpen, setRtOpen] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteMonthOpen, setDeleteMonthOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [detailRider, setDetailRider] = useState(null);
  const [listType, setListType] = useState(null);
  const [drill, setDrill] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const isMonthPeriod = period.startsWith("month:");
  const mode = (period === CUMULATIVE || period === CUMULATIVE_ALL || isMonthPeriod) ? "cumulative" : "date";
  const scope = period === CUMULATIVE_ALL ? "all" : "month";
  const dateParam = mode === "date" ? period : null;
  const [effFrom, effTo] = isMonthPeriod ? monthBounds(period.slice(6)) : [range.from || null, range.to || null];
  // Scope the rider-detail modal to the same month the panel is showing (empty => all history).
  const detailMonth = period === CUMULATIVE_ALL ? ""
    : isMonthPeriod ? period.slice(6)
    : (data?.range?.[1] ? data.range[1].slice(0, 7) : (data?.date_used ? data.date_used.slice(0, 7) : ""));

  const load = useCallback(async () => {
    if (platform === UNIFIED) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.ridersOverview(mode, dateParam, city, contract, effFrom, effTo, platform, scope, undefined, branch);
      setData(res);
    } catch { toast.error(t("dash.riders.loadFail")); }
    finally { setLoading(false); }
  }, [mode, dateParam, scope, city, contract, effFrom, effTo, t, platform, branch]);

  useEffect(() => { load(); }, [load]);

  const afterUpload = (res) => {
    if (res?.inserted) setLastUpload({ ...res, at: new Date() });
    setCity("all"); setContract("all"); setRange({ from: "", to: "" });
    const m = String(res?.last_date || res?.report_date || "").slice(0, 7);
    const cur = new Date(Date.now() + 3 * 3600e3).toISOString().slice(0, 7);
    const next = /^\d{4}-\d{2}$/.test(m) && m !== cur ? `month:${m}` : CUMULATIVE;
    if (next === period) load(); else setPeriod(next);
  };

  const doDelete = async () => {
    try {
      const res = await api.deleteRidersDate(dateParam);
      toast.success(t("dash.riders.delDayDone", { d: dateParam, n: res.deleted }));
      setDeleteOpen(false);
      setPeriod(CUMULATIVE);
      load();
    } catch { toast.error(t("dash.riders.delFail")); }
  };

  const doDeleteMonth = async () => {
    const m = period.slice(6);
    try {
      const res = await api.deleteRidersMonth(m, platform);
      toast.success(t("dash.riders.delMonthDone", { m: monthLabel(m), n: res.deleted }));
      setDeleteMonthOpen(false);
      setPeriod(CUMULATIVE);
      load();
    } catch { toast.error(t("dash.riders.delFail")); }
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const filteredRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => matchFields(r, RIDER_FIELDS, rxf) && (!s || [r.emp_name, r.rider_id, r.city, r.contract, r.vehicle, r.supervisor]
      .some((v) => String(v || "").toLowerCase().includes(s))));
  }, [rows, search, rxf]);

  const ranked = useMemo(() => {
    const valid = rows.filter((r) => typeof r[rankMetric] === "number" && !r.platform_suspended);
    const top = [...valid].sort((a, b) => b[rankMetric] - a[rankMetric]).slice(0, 10);
    const bottom = [...valid].sort((a, b) => a[rankMetric] - b[rankMetric]).slice(0, 10);
    return { top, bottom };
  }, [rows, rankMetric]);

  const noShowRows = useMemo(() => rows.filter((r) => (r.no_shows || 0) > 0).sort((a, b) => (b.no_shows || 0) - (a.no_shows || 0)), [rows]);
  const errorRows = useMemo(() => rows.filter((r) => hasError(r.error_codes)), [rows]);
  const listRows = listType === "no_shows" ? noShowRows : errorRows;

  const pctVal = (v) => (v == null ? null : (v <= 1 ? v * 100 : v));
  const drillRows = useMemo(() => {
    if (!drill) return [];
    const arr = (rows || [])
      .map((r) => ({ r, v: drill.valueOf(r) }))
      .filter((x) => x.v != null && !Number.isNaN(Number(x.v)));
    arr.sort((a, b) => (drill.asc ? a.v - b.v : b.v - a.v));
    return arr;
  }, [drill, rows]);
  const drillTotal = useMemo(() => drillRows.reduce((t, x) => t + Number(x.v || 0), 0), [drillRows]);

  const sum = data?.summary;
  const trends = data?.trends || [];
  const hasData = (data?.available_dates || []).length > 0;
  const cumulative = mode === "cumulative";

  const printPdf = () => {
    if (!filteredRows.length) { toast.warning(t("dash.riders.noMatch")); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const cols = COL_KEYS.map((c) => ({ k: c.k, t: c.t, label: t(`dash.riders.cols.${c.k}`) }));
    const head = `<th>${t("dash.riders.colRider")}</th>${cumulative ? `<th>${esc(t("dash.riders.colDays"))}</th>` : ""}` +
      cols.map((c) => `<th>${esc(c.label)}</th>`).join("");
    const body = filteredRows.map((r) => {
      const cells = cols.map((c) => `<td class="${c.t === "num" || c.t === "pct" ? "num" : ""}">${esc(fmt(r[c.k], c.t))}</td>`).join("");
      return `<tr><td class="rider">${esc(riderName(r))}${(r.emp_name || r.rider_name) ? ` <span class="rid">#${esc(r.rider_id)}</span>` : ""}</td>${cumulative ? `<td class="num">${esc(r.days ?? "—")}</td>` : ""}${cells}</tr>`;
    }).join("");
    const periodLabel = cumulative
      ? (isMonthPeriod ? `شهر (${monthLabel(period.slice(6))})`
        : (range.from || range.to) ? `${range.from || "…"} → ${range.to || "…"}`
        : scope === "all" ? "كل الأيام"
          : `الشهر الحالي (${monthLabel((data.available_dates[data.available_dates.length - 1] || "").slice(0, 7))})`)
      : dateParam;
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>${esc(t("dash.riders.title"))} — ${esc(platform)}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family:SaudiRiyal,'IBM Plex Sans Arabic','Arial',sans-serif; color:#0f172a; }
        h1 { font-size:16px; margin:0 0 2px; text-align:center; }
        .sub { text-align:center; font-size:11px; color:#475569; margin-bottom:10px; }
        table { width:100%; border-collapse:collapse; font-size:9px; }
        th,td { border:1px solid #cbd5e1; padding:3px 5px; text-align:center; white-space:nowrap; }
        thead th { background:#2563eb; color:#fff; }
        tbody tr:nth-child(even) { background:#f8fafc; }
        td.rider { text-align:right; font-weight:600; }
        td.num { direction:ltr; }
        .rid { color:#94a3b8; font-weight:400; font-size:8px; }
      </style></head><body>
      <h1>${esc(t("dash.riders.title"))} — ${esc(platform)}</h1>
      <div class="sub">${esc(periodLabel)} · ${filteredRows.length}</div>
      <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    w.document.write(html); w.document.close();
  };

  const exportNamesOrdersExcel = async () => {
    if (!filteredRows.length) { toast.warning(t("dash.riders.noMatch")); return; }
    const sorted = [...filteredRows].sort((a, b) => (b.completed_deliveries || 0) - (a.completed_deliveries || 0));
    const ExcelJS = (await import("exceljs")).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("الأسماء والطلبات", { views: [{ rightToLeft: true }] });
    ws.columns = [{ header: "#", width: 6 }, { header: t("dash.riders.colRider"), width: 34 }, { header: "رقم المندوب", width: 16 },
      { header: t("dash.riders.cols.completed_deliveries"), width: 16 }];
    ws.getRow(1).eachCell((c) => { c.font = { bold: true, color: { argb: "FFFFFFFF" } }; c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } }; c.alignment = { horizontal: "center" }; });
    sorted.forEach((r, i) => ws.addRow([i + 1, riderName(r), String(r.rider_id ?? ""), Number(r.completed_deliveries) || 0]));
    const buf = await wb.xlsx.writeBuffer();
    await saveBlob(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `names_orders_${platform}.xlsx`);
  };

  const printNamesOrders = () => {
    if (!filteredRows.length) { toast.warning(t("dash.riders.noMatch")); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const sorted = [...filteredRows].sort((a, b) => (b.completed_deliveries || 0) - (a.completed_deliveries || 0));
    const body = sorted.map((r, i) => `<tr><td class="num">${i + 1}</td><td class="rider">${esc(riderName(r))}${(r.emp_name || r.rider_name) ? ` <span class="rid">#${esc(r.rider_id)}</span>` : ""}</td><td class="num"><b>${esc(fmt(r.completed_deliveries, "num"))}</b></td></tr>`).join("");
    const periodLabel = cumulative
      ? (isMonthPeriod ? `شهر (${monthLabel(period.slice(6))})`
        : (range.from || range.to) ? `${range.from || "…"} → ${range.to || "…"}`
        : scope === "all" ? "كل الأيام"
          : `الشهر الحالي (${monthLabel((data.available_dates[data.available_dates.length - 1] || "").slice(0, 7))})`)
      : dateParam;
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>${esc(t("dash.riders.namesOrdersTitle"))} — ${esc(platform)}</title>
      <style>
        @page { size: A4 portrait; margin: 14mm; }
        body { font-family:SaudiRiyal,'IBM Plex Sans Arabic','Arial',sans-serif; color:#0f172a; }
        h1 { font-size:17px; margin:0 0 2px; text-align:center; }
        .sub { text-align:center; font-size:12px; color:#475569; margin-bottom:12px; }
        table { width:100%; border-collapse:collapse; font-size:12px; }
        th,td { border:1px solid #cbd5e1; padding:6px 10px; text-align:center; }
        thead th { background:#2563eb; color:#fff; }
        tbody tr:nth-child(even) { background:#f8fafc; }
        td.rider { text-align:right; font-weight:600; }
        td.num { direction:ltr; }
        .rid { color:#94a3b8; font-weight:400; font-size:10px; }
      </style></head><body>
      <h1>${esc(t("dash.riders.namesOrdersTitle"))} — ${esc(platform)}</h1>
      <div class="sub">${esc(periodLabel)} · ${sorted.length}</div>
      <table><thead><tr><th style="width:44px">#</th><th>${esc(t("dash.riders.colRider"))}</th><th>${esc(t("dash.riders.cols.completed_deliveries"))}</th></tr></thead><tbody>${body}</tbody></table>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    w.document.write(html); w.document.close();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="space-y-5" data-testid="riders-panel" dir={dir}>
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-l from-slate-50 to-white p-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <p className="text-xs font-semibold text-slate-500 pt-2">منصة التوصيل</p>
          <RiderPlatformSwitcher canEdit={canEdit} showUnified />
          <KeetaPurgeButton />
        </div>
      </div>
      {platform === UNIFIED ? (
        <UnifiedRidersView />
      ) : (
      <>
      {platform === "كيتا شرائح" && (
        <div className="inline-flex max-w-full flex-wrap rounded-xl border border-slate-200 bg-white p-1" data-testid="keeta-tabs">
          {[["sup", "تقرير المشرفين"], ["riders", "تقرير المناديب"], ["category", "الكاتقري"], ["face", "تحقق الوجه"], ["validity", "الصلاحية"], ["fake_gps", "Fake GPS"]].map(([k, l]) => (
            <button key={k} onClick={() => setKeetaTab(k)} data-testid={`keeta-tab-${k}`}
              className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${keetaTab === k ? "bg-amber-400 text-slate-900" : "text-slate-500 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      )}
      {platform === "كيتا شرائح" && ["category", "face", "validity", "fake_gps"].includes(keetaTab) ? <KeetaReports key={keetaTab} kind={keetaTab} canEdit={canEdit} onKind={setKeetaTab} /> :
      platform === "كيتا شرائح" && keetaTab === "sup" ? <KeetaSupervisors platform={platform} canEdit={canEdit} /> : (
      <>
      {!isKeeta(platform) && <MonthlyEvaluationSection canEdit={canEdit} />}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-blue-600 flex items-center justify-center">
            <Bike className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-800 leading-tight">{t("dash.riders.title")}</p>
            <p className="text-xs text-slate-400">{t("dash.riders.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasData && (
            <>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger data-testid="rider-period-select" className="w-48 gap-1.5">
                  {cumulative ? <Layers className="h-4 w-4 text-blue-600" /> : <CalendarDays className="h-4 w-4 text-slate-400" />}
                  <SelectValue placeholder={t("dash.riders.period")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUMULATIVE} data-testid="rider-period-cumulative">تراكمي — الشهر الحالي</SelectItem>
                  <SelectItem value={CUMULATIVE_ALL} data-testid="rider-period-cumulative-all">تراكمي — كل الأيام</SelectItem>
                  {(() => {
                    const all = [...(data.available_dates || [])].reverse();
                    if (!all.length) return null;
                    const latestMonth = all[0].slice(0, 7);
                    const current = all.filter((d) => d.slice(0, 7) === latestMonth);
                    const older = all.filter((d) => d.slice(0, 7) !== latestMonth);
                    return (
                      <>
                        <SelectGroup>
                          <SelectLabel className="text-[11px] text-slate-400 font-normal">{monthLabel(latestMonth)}</SelectLabel>
                          {current.map((d) => <SelectItem key={d} value={d}>{t("dash.riders.dayPrefix", { d })}</SelectItem>)}
                        </SelectGroup>
                        {older.length > 0 && (
                          <>
                            <div role="button" tabIndex={0} data-testid="rider-archive-toggle"
                              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setArchiveOpen((o) => !o); }}
                              className="flex items-center justify-between gap-2 px-2 py-2 mt-1 border-t border-slate-100 text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-50 rounded-md select-none">
                              <span className="flex items-center gap-1.5"><Archive className="h-3.5 w-3.5" /> الأرشيف — أشهر سابقة ({older.length})</span>
                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${archiveOpen ? "rotate-180" : ""}`} />
                            </div>
                            {archiveOpen && groupByMonth(older).map(([m, ds]) => (
                              <SelectGroup key={m}>
                                <SelectLabel className="text-[11px] text-slate-400 font-normal">{monthLabel(m)}</SelectLabel>
                                {ds.map((d) => <SelectItem key={d} value={d}>{t("dash.riders.dayPrefix", { d })}</SelectItem>)}
                              </SelectGroup>
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
              {(() => {
                const months = [...new Set((data.available_dates || []).map((d) => d.slice(0, 7)))].sort().reverse();
                if (!months.length) return null;
                return (
                  <Select value={isMonthPeriod ? period : ""} onValueChange={setPeriod}>
                    <SelectTrigger data-testid="rider-month-select" className="w-40 gap-1.5">
                      <CalendarRange className="h-4 w-4 text-amber-500" />
                      <SelectValue placeholder="اختر شهراً" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => <SelectItem key={m} value={`month:${m}`} data-testid={`rider-month-${m}`}>{monthLabel(m)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                );
              })()}
              {cumulative && !isMonthPeriod && (
                <RiderDayRange dates={data.available_dates} range={range} setRange={setRange} />
              )}
              {(data.branches || []).some(Boolean) && (
                <Select value={branch} onValueChange={setBranch}>
                  <SelectTrigger data-testid="rider-branch-select" className="w-44"><SelectValue placeholder="الفرع" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">كل الفروع</SelectItem>
                    {data.branches.map((b) => <SelectItem key={b || "__none__"} value={b || "__none__"}>{b || "بدون فرع"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger data-testid="rider-city-select" className="w-32"><SelectValue placeholder={t("dash.riders.cityPh")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("dash.riders.allCities")}</SelectItem>
                  {(data.cities || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={contract} onValueChange={setContract}>
                <SelectTrigger data-testid="rider-contract-select" className="w-40"><SelectValue placeholder={t("dash.riders.contractPh")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("dash.riders.allContracts")}</SelectItem>
                  {(data.contracts || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <a href={ridersExportUrl(mode, dateParam, city, contract, effFrom, effTo, platform, scope)} data-testid="rider-export-button"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 h-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
                <FileSpreadsheet className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash.riders.export")}</span>
              </a>
              <button type="button" onClick={printPdf} data-testid="rider-export-pdf-button"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 h-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
                <Printer className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span>
              </button>
              <button type="button" onClick={exportNamesOrdersExcel} data-testid="rider-names-orders-excel-button" title="الأسماء والطلبات Excel"
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 h-9 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition">
                <FileSpreadsheet className="h-4 w-4" /> <span className="hidden sm:inline">الأسماء والطلبات Excel</span>
              </button>
              <button type="button" onClick={printNamesOrders} data-testid="rider-names-orders-button"
                className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 h-9 text-sm font-medium text-blue-700 hover:bg-blue-100 transition">
                <Printer className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash.riders.namesOrdersBtn")}</span>
              </button>
            </>
          )}
          {canEdit && hasData && (
            <Button variant="outline" size="sm" data-testid="map-riders-button" className="gap-1.5 border-sky-200 text-sky-700 hover:bg-sky-50"
              onClick={() => setMappingOpen(true)}>
              <Link2 className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash.riders.mapNames")}</span>
            </Button>
          )}
          {canEdit && hasData && !cumulative && (
            <Button variant="outline" size="sm" data-testid="delete-rider-date-button"
              className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash.riders.delDay")}</span>
            </Button>
          )}
          {canEdit && hasData && isMonthPeriod && (
            <Button variant="outline" size="sm" data-testid="delete-rider-month-button"
              className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteMonthOpen(true)}>
              <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash.riders.delMonth")}</span>
            </Button>
          )}
          {canEdit && platform === "نينجا" && <NinjaTools />}
          {canEdit && hasData && isKeeta(platform) && (
            <Button variant="outline" size="sm" data-testid="rider-targets-btn" className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setRtOpen(true)}>
              <Target className="h-4 w-4" /> <span className="hidden sm:inline">أهداف المناديب</span>
            </Button>
          )}
          {canEdit && hasData && !isKeeta(platform) && (
            <Button variant="outline" size="sm" data-testid="reconcile-riders-button" className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={() => setReconcileOpen(true)}>
              <Scale className="h-4 w-4" /> <span className="hidden sm:inline">مطابقة</span>
            </Button>
          )}
          {canEdit && (
            <Button size="sm" data-testid="upload-riders-button" className="bg-blue-600 hover:bg-blue-700 gap-1.5"
              onClick={() => setUploadOpen(true)}>
              <Upload className="h-4 w-4" /> {t("dash.riders.uploadReport")}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400">{t("dash.riders.loading")}</div>
      ) : !hasData ? (
        <div className="py-16 text-center" data-testid="riders-empty">
          <Bike className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-1">{t("dash.riders.noReports")}</p>
          <p className="text-sm text-slate-400">{canEdit ? t("dash.riders.uploadFirst") : t("dash.riders.waitingData")}</p>
        </div>
      ) : (
        <>
          {platform !== UNIFIED && <UploadTracker params={{ section: "riders", platform, month: data?.month || "" }} rev={data} title={`رفع تقارير ${platform}`} testid="ut-riders" canEdit={canEdit} onDeleted={() => load()} />}
          {lastUpload && <UploadNotice u={lastUpload} onClose={() => setLastUpload(null)} />}
          <BranchTotals items={data.branch_totals} summary={branch === "all" ? data.summary : null} branch={branch} setBranch={setBranch} />
          <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-2.5 text-sm text-blue-800 flex items-center gap-2" data-testid="riders-mode-note">
            {cumulative
              ? <><Layers className="h-4 w-4" /> {
                  isMonthPeriod
                    ? `عرض شهر (${monthLabel(period.slice(6))}) — مجموع ${trends.length} يوم (الأرقام جُمعت والنِسب متوسّطة).`
                    : (range.from || range.to)
                      ? t("dash.riders.cumNoteRange", { from: range.from || t("dash.riders.rangeStart"), to: range.to || t("dash.riders.rangeEnd") })
                      : scope === "all"
                        ? t("dash.riders.cumNoteAll", { n: data.available_dates.length })
                        : `عرض الشهر الحالي (${monthLabel(data.month || "")}) — مجموع ${trends.length} يوم (الأرقام جُمعت والنِسب متوسّطة).`
                }</>
              : <><CalendarDays className="h-4 w-4" /> {t("dash.riders.dayNote", { d: data.date_used })}</>}
          </div>

          {isKeeta(platform) && data?.keeta_summary ? (
            <KeetaDashboard summary={data.keeta_summary} onMetricDrill={(m) => setDrill({
              title: m.label,
              desc: m.type === "yesno" ? "السائقون الذين أجابوا «نعم» — مرتبون حسب عدد الأيام" : "السائقون مرتبون حسب هذا المقياس",
              share: m.type === "count" || m.type === "duration" || m.type === "yesno",
              valueOf: (r) => {
                const v = r.keeta?.[m.key]?.v;
                if (m.type === "yesno") { if (typeof v === "string") return /^(yes|نعم|true)$/i.test(v.trim()) ? 1 : null; return v > 0 ? v : null; }
                return typeof v === "number" ? v : null;
              },
              fmtVal: m.type === "rate" ? (v) => `${(v <= 1 ? v * 100 : v).toFixed(1)}`
                : (m.type === "duration" || m.type === "minutes") ? (v) => Number(v).toFixed(1) : undefined,
              unit: m.type === "rate" ? "%" : m.type === "duration" ? " س" : m.type === "minutes" ? " د" : m.type === "yesno" ? " يوم" : "",
            })} />
          ) : platform === "نينجا" && data?.ninja_summary ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ninja-kpis">
              <KpiCard testid="nk-delivered" icon={PackageCheck} label="إجمالي الطلبات المسلّمة" value={fmt(data.ninja_summary.total_delivered, "num")} tone="sky" onClick={() => setDrill({ title: "إجمالي الطلبات المسلّمة", desc: "الكباتن مرتبون حسب عدد الطلبات المسلّمة", valueOf: (r) => r.completed_deliveries, unit: " طلب" })} />
              <KpiCard testid="nk-captains" icon={Users} label="الكباتن النشطون" value={data.ninja_summary.active_captains} tone="emerald" onClick={() => setDrill({ title: "الكباتن النشطون", desc: "جميع الكباتن النشطين مرتبون حسب الطلبات", valueOf: (r) => r.completed_deliveries, unit: " طلب" })} />
              {data.ninja_summary.total_orders > 0 && (<>
                <KpiCard testid="nk-orders" icon={PackageCheck} label="إجمالي الطلبات" value={fmt(data.ninja_summary.total_orders, "num")} onClick={() => setDrill({ title: "إجمالي الطلبات", desc: "الكباتن مرتبون حسب عدد الطلبات", valueOf: (r) => r.ninja_orders, unit: " طلب" })} />
                <KpiCard testid="nk-cancelled" icon={AlertTriangle} label="الطلبات الملغية" value={fmt(data.ninja_summary.cancelled, "num")} tone="amber" onClick={() => setDrill({ title: "الطلبات الملغية", desc: "الكباتن مرتبون حسب الطلبات الملغية", valueOf: (r) => r.cancelled_deliveries, unit: " طلب" })} />
                <KpiCard testid="nk-avg-order-min" icon={Clock} label="متوسط مدة الطلب (دقيقة)" value={data.ninja_summary.avg_order_min} tone="sky" onClick={() => setDrill({ title: "متوسط مدة الطلب", desc: "الكباتن مرتبون حسب متوسط مدة الطلب", valueOf: (r) => (r.ninja_timed ? r.ninja_order_min_total / r.ninja_timed : 0), fmtVal: (v) => Number(v).toFixed(1), unit: " دقيقة" })} />
                <KpiCard testid="nk-payable-km" icon={TrendingUp} label="المسافة المستحقة (كم)" value={fmt(Math.round((data.ninja_summary.pickup_km + data.ninja_summary.delivery_km) * 10) / 10, "num")} tone="emerald" onClick={() => setDrill({ title: "المسافة المستحقة (للمطعم + للعميل)", desc: "الكباتن مرتبون حسب المسافة المستحقة", valueOf: (r) => (r.ninja_pickup_km || 0) + (r.ninja_delivery_km || 0), fmtVal: (v) => Number(v).toFixed(1), unit: " كم" })} />
                <KpiCard testid="nk-pickup-km" icon={TrendingUp} label="مسافة الاستلام من المطعم (كم)" value={fmt(data.ninja_summary.pickup_km, "num")} onClick={() => setDrill({ title: "مسافة الاستلام", desc: "الكباتن مرتبون حسب مسافة الاستلام", valueOf: (r) => r.ninja_pickup_km, fmtVal: (v) => Number(v).toFixed(1), unit: " كم" })} />
                <KpiCard testid="nk-delivery-km" icon={TrendingUp} label="مسافة التوصيل للعميل (كم)" value={fmt(data.ninja_summary.delivery_km, "num")} onClick={() => setDrill({ title: "مسافة التوصيل", desc: "الكباتن مرتبون حسب مسافة التوصيل", valueOf: (r) => r.ninja_delivery_km, fmtVal: (v) => Number(v).toFixed(1), unit: " كم" })} />
                <KpiCard testid="nk-cod" icon={PackageCheck} label="التحصيل النقدي (ر.س)" value={fmt(data.ninja_summary.cod, "num")} tone="emerald" onClick={() => setDrill({ title: "التحصيل عند التسليم", desc: "الكباتن مرتبون حسب المبالغ المحصّلة", valueOf: (r) => r.ninja_cod, unit: " ر.س" })} />
                <KpiCard testid="nk-cash-outage" icon={AlertTriangle} label="العجز النقدي (ر.س)" value={fmt(data.ninja_summary.cash_outage, "num")} tone="amber" onClick={() => setDrill({ title: "العجز النقدي", desc: "الكباتن مرتبون حسب العجز النقدي", valueOf: (r) => r.ninja_cash_outage, unit: " ر.س" })} />
              </>)}
              {data.ninja_summary.total_hours > 0 && (<>
              <KpiCard testid="nk-total-hours" icon={Clock} label="إجمالي ساعات العمل" value={fmt(data.ninja_summary.total_hours, "num")} onClick={() => setDrill({ title: "إجمالي ساعات العمل", desc: "الكباتن مرتبون حسب ساعات العمل", valueOf: (r) => r.actual_hours, unit: " ساعة" })} />
              <KpiCard testid="nk-avg-hours" icon={Clock} label="متوسط ساعات العمل/كابتن" value={data.ninja_summary.avg_hours} tone="amber" onClick={() => setDrill({ title: "ساعات العمل لكل كابتن", desc: "الكباتن مرتبون حسب ساعات العمل", valueOf: (r) => r.actual_hours, unit: " ساعة" })} />
              <KpiCard testid="nk-avg-orders" icon={PackageCheck} label="متوسط الطلبات/كابتن" value={data.ninja_summary.avg_orders} tone="emerald" onClick={() => setDrill({ title: "الطلبات لكل كابتن", desc: "الكباتن مرتبون حسب عدد الطلبات", valueOf: (r) => r.completed_deliveries, unit: " طلب" })} />
              <KpiCard testid="nk-utr" icon={TrendingUp} label="معدل الطلبات بالساعة (UTR)" value={data.ninja_summary.utr} tone="sky" onClick={() => setDrill({ title: "معدل الطلبات بالساعة (UTR)", desc: "الكباتن مرتبون حسب معدل الطلبات بالساعة", valueOf: (r) => r.ninja_utr, fmtVal: (v) => Number(v).toFixed(2) })} />
              <KpiCard testid="nk-ontime" icon={CheckCircle2} label="التوصيل في الوقت المحدد" value={`${(data.ninja_summary.ontime_pct * 100).toFixed(1)}%`} tone="emerald" onClick={() => setDrill({ title: "التوصيل في الوقت المحدد", desc: "الكباتن مرتبون حسب نسبة التوصيل في الوقت", valueOf: (r) => pctVal(r.ninja_ontime), fmtVal: (v) => Number(v).toFixed(1), unit: "%" })} />
              <KpiCard testid="nk-busy" icon={TrendingUp} label="نسبة الانشغال" value={`${(data.ninja_summary.busy_util_pct * 100).toFixed(1)}%`} tone="sky" onClick={() => setDrill({ title: "نسبة الانشغال", desc: "الكباتن مرتبون حسب نسبة الانشغال", valueOf: (r) => pctVal(r.ninja_busy_util), fmtVal: (v) => Number(v).toFixed(1), unit: "%" })} />
              <KpiCard testid="nk-delay" icon={AlertTriangle} label="متوسط وقت التأخير (دقيقة)" value={fmt(data.ninja_summary.avg_delay_min, "num")} tone="amber" onClick={() => setDrill({ title: "وقت التأخير", desc: "الكباتن الأكثر تأخيراً (بالدقائق)", valueOf: (r) => r.ninja_delay_min, unit: " دقيقة" })} />
              <KpiCard testid="nk-shifts" icon={CalendarDays} label="عدد الورديات" value={fmt(data.ninja_summary.total_shifts, "num")} onClick={() => setDrill({ title: "عدد الورديات", desc: "الكباتن مرتبون حسب عدد الورديات", valueOf: (r) => r.shifts != null ? r.shifts : r.working_days, unit: " وردية" })} />
              <KpiCard testid="nk-checkin" icon={CheckCircle2} label="مرات تسجيل الحضور" value={fmt(data.ninja_summary.checked_in, "num")} tone="emerald" onClick={() => setDrill({ title: "مرات تسجيل الحضور", desc: "الكباتن مرتبون حسب مرات تسجيل الحضور", valueOf: (r) => r.ninja_checked_in })} />
              <KpiCard testid="nk-busy-time" icon={Clock} label="إجمالي وقت الانشغال (ساعة)" value={fmt(data.ninja_summary.total_busy, "num")} tone="sky" onClick={() => setDrill({ title: "وقت الانشغال", desc: "الكباتن مرتبون حسب وقت الانشغال", valueOf: (r) => r.ninja_busy, unit: " ساعة" })} />
              <KpiCard testid="nk-free-time" icon={Clock} label="إجمالي الوقت الحر (ساعة)" value={fmt(data.ninja_summary.total_free, "num")} tone="amber" onClick={() => setDrill({ title: "الوقت الحر", desc: "الكباتن مرتبون حسب الوقت الحر", valueOf: (r) => r.ninja_free, unit: " ساعة" })} />
              <KpiCard testid="nk-offline" icon={AlertTriangle} label="إجمالي عدم الاتصال (ساعة)" value={fmt(data.ninja_summary.total_offline, "num")} tone="amber" onClick={() => setDrill({ title: "عدم الاتصال", desc: "الكباتن الأكثر عدم اتصال (بالساعات)", valueOf: (r) => r.ninja_offline, unit: " ساعة" })} />
              <KpiCard testid="nk-away" icon={AlertTriangle} label="الخروج عن النطاق (ساعة)" value={fmt(data.ninja_summary.total_away, "num")} tone="amber" onClick={() => setDrill({ title: "الخروج عن النطاق", desc: "الكباتن الأكثر خروجاً عن النطاق (بالساعات)", valueOf: (r) => r.ninja_away, unit: " ساعة" })} />
              </>)}
            </div>
          ) : sum && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="riders-kpis">
              <KpiCard testid="kpi-total-riders" icon={Users} label={t("dash.riders.kTotal")} value={sum.total_riders} onClick={() => setDrill({ title: t("dash.riders.kTotal"), desc: "جميع المناديب مرتبون حسب الطلبات", valueOf: (r) => r.completed_deliveries, unit: " طلب" })} />
              <KpiCard testid="kpi-completed" icon={PackageCheck} label={t("dash.riders.kCompleted")} value={fmt(sum.total_completed, "num")} tone="sky" onClick={() => setDrill({ title: t("dash.riders.kCompleted"), desc: "المناديب مرتبون حسب الطلبات المكتملة", valueOf: (r) => r.completed_deliveries, unit: " طلب" })} />
              <KpiCard testid="kpi-attendance" icon={CheckCircle2} label={t("dash.riders.kAttendance")} value={`${(sum.avg_attendance * 100).toFixed(1)}%`} onClick={() => setDrill({ title: t("dash.riders.kAttendance"), desc: "المناديب مرتبون حسب نسبة الحضور", valueOf: (r) => pctVal(r.attendance_rate), fmtVal: (v) => Number(v).toFixed(1), unit: "%" })} />
              <KpiCard testid="kpi-acceptance" icon={TrendingUp} label={t("dash.riders.kAcceptance")} value={`${(sum.avg_acceptance * 100).toFixed(1)}%`} tone="sky" onClick={() => setDrill({ title: t("dash.riders.kAcceptance"), desc: "المناديب مرتبون حسب نسبة القبول", valueOf: (r) => pctVal(r.acceptance_rate), fmtVal: (v) => Number(v).toFixed(1), unit: "%" })} />
              <KpiCard testid="kpi-hours" icon={Clock} label={t("dash.riders.kHours")} value={fmt(sum.total_hours, "num")} onClick={() => setDrill({ title: t("dash.riders.kHours"), desc: "المناديب مرتبون حسب ساعات العمل", valueOf: (r) => r.actual_hours, unit: " ساعة" })} />
              <KpiCard testid="kpi-noshows" icon={AlertTriangle} label={t("dash.riders.kNoShows")} value={fmt(sum.total_no_shows, "num")} tone="amber" onClick={() => setListType("no_shows")} />
              <KpiCard testid="kpi-chat" icon={TrendingUp} label={t("dash.riders.kChat")} value={`${(sum.avg_chat * 100).toFixed(1)}%`} tone="sky" onClick={() => setDrill({ title: t("dash.riders.kChat"), desc: "المناديب مرتبون حسب الرد على المحادثات", valueOf: (r) => pctVal(r.chat_response_pct), fmtVal: (v) => Number(v).toFixed(1), unit: "%" })} />
              <KpiCard testid="kpi-compliant" icon={CheckCircle2} label={t("dash.riders.kCompliant")} value={sum.compliant} onClick={() => setDrill({ title: t("dash.riders.kCompliant"), desc: "المناديب الملتزمون مرتبون حسب الطلبات", valueOf: (r) => ((r.tga_status || "").trim().toLowerCase() === "compliant" ? r.completed_deliveries || 0 : null), unit: " طلب" })} />
              <KpiCard testid="kpi-errors" icon={Bug} label={t("dash.riders.kErrors")} value={errorRows.length} tone="amber" onClick={() => setListType("error_codes")} />
            </div>
          )}

          {/* Top / bottom 10 */}
          <div className="space-y-3" data-testid="riders-ranking">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="font-heading font-bold text-slate-700">{t("dash.riders.topBottom")}</p>
              <Select value={rankMetric} onValueChange={setRankMetric}>
                <SelectTrigger data-testid="rank-metric-select" className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed_deliveries">{t("dash.riders.byCompleted")}</SelectItem>
                  <SelectItem value="attendance_rate">{t("dash.riders.byAttendance")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RankList title={t("dash.riders.top10")} icon={TrendingUp} tone="emerald" rows={ranked.top} metric={rankMetric} onSelect={setDetailRider} />
              <RankList title={t("dash.riders.bottom10")} icon={TrendingDown} tone="amber" rows={ranked.bottom} metric={rankMetric} onSelect={setDetailRider} />
            </div>
          </div>

          {trends.length > 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="riders-trends">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="font-heading font-bold text-slate-700 text-sm mb-3">{t("dash.riders.trendCompleted")}</p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trends} margin={{ left: -10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="completed" name={t("dash.riders.completed")} stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="font-heading font-bold text-slate-700 text-sm mb-3">{t("dash.riders.trendRates")}</p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trends} margin={{ left: -10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="attendance" name={t("dash.riders.attendancePct")} stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                    <Line type="monotone" dataKey="acceptance" name={t("dash.riders.acceptancePct")} stroke="#0284c7" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-slate-500">{t("dash.riders.showingCount", { shown: filteredRows.length, total: rows.length })}</p>
            <div className="w-full md:w-auto md:flex-1"><ExtraFilters rows={rows} fields={RIDER_FIELDS} xf={rxf} setXf={setRxf} presetKey="riders" snapshot={{ rxf, search }}
              onApply={(st) => { setRxf(st.rxf || {}); setSearch(st.search || ""); }} /></div>
            <div className="relative w-full md:w-64">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="rider-search-input" placeholder={t("dash.riders.searchNameNum")} value={search}
                onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="riders-table">
            <table className="w-full text-sm text-right border-collapse min-w-max">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
                  <th className="px-3 py-2.5 font-semibold whitespace-nowrap sticky right-0 bg-slate-50 z-10">{t("dash.riders.colRider")}</th>
                  <th className="px-3 py-2.5 font-semibold whitespace-nowrap text-center">{t("dash.riders.colGrade")}</th>
                  <th className="px-3 py-2.5 font-semibold whitespace-nowrap text-center">{t("dash.riders.colTarget")}</th>
                  {isKeeta(platform) && <th className="px-3 py-2.5 font-semibold whitespace-nowrap text-center">الأهداف</th>}
                  {cumulative && <th className="px-3 py-2.5 font-semibold whitespace-nowrap">{t("dash.riders.colDays")}</th>}
                  <th className="px-3 py-2.5 font-semibold whitespace-nowrap">{t("dash.riders.colChange")}</th>
                  {COL_KEYS.map((c) => <th key={c.k} className="px-3 py-2.5 font-semibold whitespace-nowrap">{t(`dash.riders.cols.${c.k}`)}</th>)}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.rider_id} data-testid={`rider-row-${r.rider_id}`} onClick={() => setDetailRider(r.rider_id)}
                    className="border-b border-slate-100 hover:bg-blue-50/50 transition cursor-pointer">
                    <td className="px-3 py-2 whitespace-nowrap sticky right-0 bg-white z-10">
                      <div className="font-semibold text-slate-800">{r.emp_name || r.rider_name || r.rider_id}</div>
                      {(r.emp_name || r.rider_name) && <div className="text-[10px] text-slate-400 font-mono-nums">#{r.rider_id}</div>}
                      {r.branch && <div className="mt-0.5 inline-flex rounded-full bg-blue-50 px-1.5 py-px text-[10px] font-semibold text-blue-700" data-testid={`rider-branch-${r.rider_id}`}>{r.branch}</div>}
                      {r.supervisor && <div className="mt-0.5 inline-flex rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700" data-testid={`rider-sup-${r.rider_id}`}>المشرف: {r.supervisor}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center"><GradeBadge grade={r.grade} percent={r.grade_percent} /></td>
                    <td className="px-3 py-2 whitespace-nowrap text-center"><TargetCell row={r} /></td>
                    {isKeeta(platform) && <td className="px-3 py-2 whitespace-nowrap text-center"><RTBadge kt={r.ktargets} id={r.rider_id} /></td>}
                    {cumulative && <td className="px-3 py-2 whitespace-nowrap text-slate-600">{r.days ?? "—"}</td>}
                    <td className="px-3 py-2 whitespace-nowrap"><ChangeCell row={r} metric={rankMetric} /></td>
                    {COL_KEYS.map((c) => (
                      <td key={c.k} className="px-3 py-2 whitespace-nowrap text-slate-700">
                        {c.t === "status" ? (
                          <Badge variant="outline" className={`rounded-full text-[10px] ${String(r[c.k]).toLowerCase() === "compliant" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                            {fmt(r[c.k], c.t)}
                          </Badge>
                        ) : fmt(r[c.k], c.t)}
                      </td>
                    ))}
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr><td colSpan={COL_KEYS.length + (cumulative ? 5 : 4) + (isKeeta(platform) ? 1 : 0)} className="px-3 py-10 text-center text-slate-400">{t("dash.riders.noMatch")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={afterUpload} />
      <ReconcileDialog open={reconcileOpen} onOpenChange={setReconcileOpen} onApplied={load} />
      <MappingDialog open={mappingOpen} onOpenChange={setMappingOpen} onSaved={load} />
      <RiderDetailDialog riderId={detailRider} month={detailMonth} onClose={() => setDetailRider(null)} row={(data?.rows || []).find((x) => x.rider_id === detailRider)} />
      {isKeeta(platform) && <RiderTargetsDialog open={rtOpen} onClose={() => setRtOpen(false)} platform={platform} month={detailMonth || new Date().toISOString().slice(0, 7)} riders={data?.rows || []} onSaved={load} />}

      <Dialog open={Boolean(listType)} onOpenChange={(v) => !v && setListType(null)}>
        <DialogContent className="sm:max-w-md font-body max-h-[85vh] overflow-y-auto" dir={dir} data-testid="rider-list-dialog">
          <DialogHeader className="text-right">
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              {listType === "no_shows" ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <Bug className="h-5 w-5 text-amber-600" />}
              {listType === "no_shows" ? t("dash.riders.listNoShow") : t("dash.riders.listErrors")}
            </DialogTitle>
            <DialogDescription className="text-right">
              {listType === "no_shows"
                ? t("dash.riders.listNoShowDesc", { n: noShowRows.length })
                : t("dash.riders.listErrDesc", { n: errorRows.length })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            {listRows.length === 0 ? (
              <div className="py-10 text-center text-slate-400">
                <CheckCircle2 className="h-8 w-8 mx-auto text-blue-400 mb-2" />
                {t("dash.riders.listEmpty")}
              </div>
            ) : listRows.map((r) => (
              <button key={r.rider_id} data-testid={`list-row-${r.rider_id}`}
                onClick={() => { setDetailRider(r.rider_id); setListType(null); }}
                className="w-full flex items-center gap-2 rounded-lg border border-slate-100 hover:bg-amber-50/60 px-3 py-2 text-right transition">
                <div className="h-8 w-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold shrink-0">
                  {(r.emp_name || r.rider_name || r.rider_id || "؟").charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">{r.emp_name || r.rider_name || r.rider_id}</p>
                  <p className="text-[10px] text-slate-400 font-mono-nums">#{r.rider_id}{r.branch ? ` · ${r.branch}` : ""}{r.city ? ` · ${r.city}` : ""}{r.supervisor ? ` · المشرف: ${r.supervisor}` : ""}</p>
                  {listType === "error_codes" && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {splitCodes(r.error_codes).map((c, i) => (
                        <span key={i} className="inline-flex items-center gap-1 rounded-md bg-amber-100 text-amber-800 text-[11px] font-bold px-1.5 py-0.5">
                          <Bug className="h-3 w-3" /> {t("dash.riders.codePrefix", { c })}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {listType === "no_shows" && (
                  <span className="mr-auto shrink-0 text-sm font-bold text-amber-700 tabular-nums">{t("dash.riders.absenceSuffix", { n: fmt(r.no_shows, "num") })}</span>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(drill)} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="sm:max-w-md font-body max-h-[85vh] overflow-y-auto" dir={dir} data-testid="kpi-drill-dialog">
          <DialogHeader className="text-right">
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" /> {drill?.title}
            </DialogTitle>
            <DialogDescription className="text-right">
              {drill?.desc} · {drillRows.length}{drill?.share && drillTotal > 0 ? ` · الإجمالي ${fmt(Math.round(drillTotal * 10) / 10, "num")}${drill.unit || ""}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            {drillRows.length === 0 ? (
              <div className="py-10 text-center text-slate-400">
                <Package className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                لا توجد بيانات تفصيلية
              </div>
            ) : drillRows.map(({ r, v }, i) => (
              <button key={r.rider_id} data-testid={`drill-row-${r.rider_id}`}
                onClick={() => { setDetailRider(r.rider_id); setDrill(null); }}
                className="w-full flex items-center gap-2.5 rounded-lg border border-slate-100 hover:bg-blue-50/60 px-3 py-2 text-right transition">
                <span className="h-6 w-6 shrink-0 rounded-full bg-slate-100 text-slate-500 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold shrink-0">
                  {(r.emp_name || r.rider_name || r.rider_id || "؟").charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">{r.emp_name || r.rider_name || r.rider_id}</p>
                  <p className="text-[10px] text-slate-400 font-mono-nums">#{r.rider_id}{r.branch ? ` · ${r.branch}` : ""}{r.city ? ` · ${r.city}` : ""}{r.supervisor ? ` · المشرف: ${r.supervisor}` : ""}</p>
                </div>
                <span className="mr-auto shrink-0 text-left">
                  <span className="block text-sm font-bold text-blue-700 tabular-nums">{drill?.fmtVal ? drill.fmtVal(v) : fmt(v, "num")}{drill?.unit || ""}</span>
                  {drill?.share && drillTotal > 0 && <span className="block text-[10px] text-slate-400 tabular-nums" data-testid={`drill-share-${r.rider_id}`}>{((v / drillTotal) * 100).toFixed(1)}% من الإجمالي</span>}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-right">{t("dash.riders.delDayTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              {t("dash.riders.delDayDesc", { d: dateParam })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-delete-rider-date-button" onClick={doDelete} className="bg-red-600 hover:bg-red-700">{t("dash.riders.del")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.riders.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteMonthOpen} onOpenChange={setDeleteMonthOpen}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-right">{t("dash.riders.delMonthTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              {t("dash.riders.delMonthDesc", { m: isMonthPeriod ? monthLabel(period.slice(6)) : "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-delete-rider-month-button" onClick={doDeleteMonth} className="bg-red-600 hover:bg-red-700">{t("dash.riders.del")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.riders.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {getPlatform() === "هنجرستيشن" && <HsReportsSection exclude={["distances"]} />}
      <PlatformReportsSection />
      </>
      )}
      </>
      )}
    </motion.div>
  );
};
